import { STORAGE_KEYS } from '@constants/storage';

import { ComponentType } from '../types';
import { getBranchSegment, getUserId, ScopedStorage } from './storageSegments';

/**
 * Outer scope = userId, inner scope = orgId:branchId → string[].
 * Each user gets their own nested map so logout preserves only one key.
 */
type BranchViews = Record<string, string[]>;

const store = new ScopedStorage<BranchViews>(
  STORAGE_KEYS.DYNAMIC_COMPONENT_MIXPANEL_VIEWS,
  {}
);

const getBranchKeys = (): Set<string> => {
  const views = store.read(getUserId());
  const arr = views[getBranchSegment()];
  if (!Array.isArray(arr)) return new Set();
  return new Set(
    arr.filter((k): k is string => typeof k === 'string' && k.trim().length > 0)
  );
};

/** Stable id for deduping Mixpanel `DYNAMIC_COMPONENT_VIEWED` per user. */
export const buildDynamicComponentViewKey = (
  componentType: ComponentType,
  id: string
): string => `${componentType}:${String(id).trim()}`;

/**
 * Returns true only the first time this user+branch has recorded a view for
 * `instanceKey`, then persists. Each dynamic component instance (banner/popup
 * id) therefore contributes at most one "viewed" event per user per branch,
 * including across carousel rotation and re-renders.
 */
export function tryMarkMixpanelViewSent(instanceKey: string): boolean {
  const normalizedKey = instanceKey.trim();
  if (!normalizedKey) return false;

  const keys = getBranchKeys();
  if (keys.has(normalizedKey)) return false;

  keys.add(normalizedKey);

  const user = getUserId();
  const branch = getBranchSegment();
  const current = store.read(user);
  store.write(user, { ...current, [branch]: [...keys] });
  return true;
}
